import * as React from 'react';
import { Link } from 'gatsby';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedinIn,
  faFacebookF,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { useLocation, navigate } from '@reach/router';
import clsx from 'clsx';
import * as icons from '../icons/icon-pack';
import useWindowSize from '../hooks/useWindowSize';
import { extractLanguage } from '../helpers/common';
import { useSlug } from '../contexts/SlugContext';

library.add(faLinkedinIn, faFacebookF, faInstagram, faLocationDot);
library.add({ ...icons });

function pathnameMatchesSlug(pathname, fullSlug) {
  let slug = fullSlug;
  if (slug.startsWith('/')) {
    slug = slug.replace('/', '');
  }

  if (slug.slice(-1) === '/') {
    slug = slug.substring(0, slug.length - 1);
  }

  return pathname.includes(slug) || slug.includes(pathname);
}

function Header({ setIsNavOpen, headerEntries }) {
  const handleLanguageSwitcherClick = (event, targetUrl) => {
    setIsNavOpen(false);
    document.body.classList.remove('lock-scroll');

    // Check if launchplan form exists on the page
    // If it does, force a full page reload for Klaviyo to work properly
    if (typeof window !== 'undefined') {
      const launchplanForm = document.getElementById('launchplan-form');
      if (launchplanForm) {
        event.preventDefault();
        if (targetUrl) {
          window.location.href = targetUrl;
        }
        return;
      }
    }
  };

  const windowSize = useWindowSize();

  // initiate isNavOpen state with false
  const handleNavToggle = () => {
    if (windowSize.width >= 1473) return;
    window.__setBodyScrollLock(false);
  };

  const handleNavigation = (to) => {
    handleNavToggle(); // Close the menu
    setTimeout(() => {
      navigate(to); // Navigate after menu animation
      setIsNavOpen(false);
    }, 400);
  };

  const location = useLocation();

  const currentLanguage = extractLanguage(location.pathname);
  const isEnglishPage = currentLanguage === 'en';

  const languageScopedEntries = headerEntries.filter((entry) => entry?.node?.lang === currentLanguage);
  const activeHeaderEntry = languageScopedEntries[0] || headerEntries[0];
  const activeMenuContent = React.useMemo(() => {
    if (!activeHeaderEntry?.node?.content) return null;

    try {
      return JSON.parse(activeHeaderEntry.node.content);
    } catch (_) {
      return null;
    }
  }, [activeHeaderEntry]);

  const { alternateSlug } = useSlug();

  return (
    <>
      <div className="w-full m-auto">
        <ul className="flex flex-col items-end lg:items-start gap-4 px-4 py-4 leading-10 lg:text-left lg:mt-8 lg:gap-8 lg:w-fit lg:px-6 lg:py-12">
          {(activeMenuContent?.Menu || []).filter((menu) => {
              const fullSlug = menu?.full_slug || '';
              const hasEnglishPrefix = fullSlug.startsWith('en/');

              // Fall back to slug-based language inference when `menu.lang` is absent.
              if (!menu?.lang) {
                return isEnglishPage ? hasEnglishPrefix : !hasEnglishPrefix;
              }

              return menu.lang === currentLanguage;
            }).map((menu, index) => {
              let prefix;
              let iconName;

              if (menu.content?.Icon) {
                iconName = menu.content?.Icon[0]?.content?.icon_title;
                prefix = menu.content?.Icon[0]?.content?.prefix;
              }

              const readyIcon = icon({ prefix, iconName });
              const isActiveMenuItem = pathnameMatchesSlug(location.pathname, menu.full_slug);
              const isContactMenuItem = menu.full_slug.includes('contact');

              return (
                <li className="relative flex" key={`${menu._uid || menu.uuid || menu.full_slug || 'menu'}-${index}`}>
                  <Link
                    className={clsx(
                      'relative text-lg lg:text-xl flex items-end lg:items-start',
                      'menu-item-before',
                      'before:absolute before:top-[-0.5rem] before:h-[calc(100%+1rem)] before:z-0 transition-[width] duration-300',
                      'before:left-auto before:lg:left-[-4rem] before:right-[-4rem] lg:before:right-auto',
                      'before:rounded-l-3xl before:lg:rounded-l-none before:lg:rounded-r-3xl',
                      {
                        active: isActiveMenuItem || isContactMenuItem,
                        inactive: !isActiveMenuItem && !isContactMenuItem,
                        contact: isContactMenuItem,
                      },
                    )}
                    to={`/${menu.full_slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(`/${menu.full_slug}`);
                    }}
                    title={menu.full_slug}
                  >
                    <div className="relative flex flex-row items-center justify-end gap-4">
                      <div className="flex-shrink-0 w-[32px] flex justify-start">
                        <FontAwesomeIcon
                          icon={readyIcon}
                          size="1x"
                          className="w-[32px] h-[32px]"
                        />
                      </div>
                      <div className="flex w-full menu-label xll:hidden -order-1 xll:order-2 whitespace-nowrap">
                        {menu.content?.Name || menu.name}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
        </ul>
        {activeMenuContent?.language_switcher && (
          <div
            className="relative flex flex-row-reverse items-center w-full gap-4 px-4 py-4 leading-10 language-switcher flex-start lg:flex-row lg:text-left lg:gap-4 lg:w-fit lg:px-6  lg:justify-end xll:justify-start"
          >
            <div className="flex-shrink-0 w-[24px] flex justify-center">
              <FontAwesomeIcon icon={faGlobe} size="1x" />
            </div>
            <div className="flex flex-row gap-2">
              <Link
                to={`${alternateSlug?.en?.startsWith('/') ? alternateSlug.en : `/${alternateSlug.en}`}`}
                aria-label={currentLanguage === 'en' ? 'English (current language)' : currentLanguage === 'default' ? 'Zum Englischen wechseln' : 'Switch to English'}
                className={`language-link ${
                  currentLanguage === 'en' ? 'active' : ''
                }`}
                onClick={(e) =>
                  handleLanguageSwitcherClick(
                    e,
                    `${alternateSlug?.en?.startsWith('/') ? alternateSlug.en : `/${alternateSlug.en}`}`,
                  )
                }
              >
                EN
              </Link>
              <span>|</span>
              <Link
                to={`${alternateSlug?.de?.startsWith('/') ? alternateSlug.de : `/${alternateSlug.de}`}`}
                aria-label={currentLanguage === 'default' ? 'Deutsch (aktuelle Sprache)' : currentLanguage === 'en' ? 'Switch to German' : 'Deutsch (aktuelle Sprache)'}
                className={`language-link ${
                  currentLanguage === 'default' ? 'active' : ''
                }`}
                onClick={(e) =>
                  handleLanguageSwitcherClick(
                    e,
                    `${alternateSlug?.de?.startsWith('/') ? alternateSlug.de : `/${alternateSlug.de}`}`,
                  )
                }
              >
                DE
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Header;
